import React, { useEffect, useState } from "react";
import {
  getForums,
  getForumDetails,
  addForum,
  addDiscussion,
  deleteDiscussion,
  deleteForum,
  getLearnersById,
  getAdminById
} from "../api";
import axios from "axios";
import "./forum.css";

const API_BASE_URL = 'https://care-api.equily.ng/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken");
  return {
    Authorization: `Bearer ${token}`,
    Accept: "application/json",
    "Content-Type": "application/json",
  };
};

const Forum = () => {
  const [forums, setForums] = useState([]);
  const [newForumTitle, setNewForumTitle] = useState("");
  const [newForumDescription, setNewForumDescription] = useState("");
  const [expandedForumId, setExpandedForumId] = useState(null);
  const [selectedForum, setSelectedForum] = useState(null);
  const [discussions, setDiscussions] = useState([]);
  const [newDiscussion, setNewDiscussion] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const storedUser = localStorage.getItem("user");
const currentUser = storedUser ? JSON.parse(storedUser) : null;
const currentUserId = currentUser?.id || 1; // fallback to 1 if needed


  useEffect(() => {
    fetchForums();
  }, []);

  const fetchForums = async () => {
    try {
      const res = await getForums(1);
      const list = Array.isArray(res)
        ? res
        : res?.data || res?.forums || [];
      
      // Fetch creator details for each forum
      const forumsWithCreators = await Promise.all(
        list.map(async (forum) => {
          // Extract creator information - check if admin/staff objects exist and have data
          let creator = null;
          if (forum.admin && (forum.admin.id || forum.admin.firstname || forum.admin.first_name || forum.admin.name || forum.admin.email)) {
            creator = forum.admin;
          } else if (forum.staff && (forum.staff.id || forum.staff.firstname || forum.staff.first_name || forum.staff.name || forum.staff.email)) {
            creator = forum.staff;
          }

          // Always try to fetch creator details if we have an ID (API might not include relationships in list)
          if (forum.admin_id || forum.staff_id) {
            // Only fetch if we don't already have valid creator data with name information
            const hasValidCreatorData = creator && (
              creator.firstname || 
              creator.first_name || 
              creator.name || 
              creator.full_name ||
              creator.email
            );
            
            if (!hasValidCreatorData) {
              try {
                const creatorId = forum.admin_id || forum.staff_id;
                if (forum.admin_id) {
                  // Fetch admin details
                  const adminData = await getAdminById(creatorId);
                  if (adminData) {
                    creator = adminData;
                  }
                } else if (forum.staff_id) {
                  // Fetch staff details
                  try {
                    const staffRes = await axios.get(`${API_BASE_URL}/staff/${creatorId}`, {
                      headers: getAuthHeaders(),
                    });
                    const staffData = staffRes.data?.data || staffRes.data;
                    if (staffData) {
                      creator = staffData;
                    }
                  } catch (staffErr) {
                    console.error(`Failed to fetch staff ${creatorId}:`, staffErr);
                    // Try as learner
                    try {
                      const learnerData = await getLearnersById(creatorId);
                      if (learnerData) {
                        creator = learnerData;
                      }
                    } catch (learnerErr) {
                      console.error(`Failed to fetch as learner ${creatorId}:`, learnerErr);
                    }
                  }
                }
              } catch (err) {
                console.error(`Failed to fetch creator ${forum.admin_id || forum.staff_id}:`, err);
              }
            }
          }

          // Get creator name - only set to "Unknown" if we truly have no data
          let creatorName = "Unknown";
          if (creator) {
            creatorName = creator.full_name || 
                        creator.name || 
                        (creator.firstname && creator.lastname ? `${creator.firstname} ${creator.lastname}`.trim() : null) ||
                        (creator.first_name && creator.last_name ? `${creator.first_name} ${creator.last_name}`.trim() : null) ||
                        creator.email ||
                        "Unknown";
          }

          // Get creator details - only if we have creator data
          const creatorPhone = creator ? (creator.phone_number || creator.phone || "N/A") : "N/A";
          const creatorFacility = creator ? (
            creator.facility?.name || 
            creator.facility_name || 
            (typeof creator.facility === 'string' ? creator.facility : null) ||
            "N/A"
          ) : "N/A";
          const creatorLGA = creator ? (typeof creator.lga === 'object' ? creator.lga?.name : creator.lga) || (typeof creator.local_government === 'object' ? creator.local_government?.name : creator.local_government) || "N/A" : "N/A";

          return {
            ...forum,
            creatorDetails: {
              name: creatorName,
              phone: creatorPhone,
              facility: creatorFacility,
              lga: creatorLGA,
              email: creator?.email || "N/A"
            }
          };
        })
      );

      setForums(forumsWithCreators);
    } catch (err) {
      console.error("Error fetching forums:", err);
      setForums([]);
    }
  };

  const fetchForumDetails = async (forumId) => {
    try {
      const res = await getForumDetails(forumId);
      let forum = res?.data || res;

      // Fetch creator details if not already included
      if ((forum.admin_id || forum.staff_id) && (!forum.admin || !forum.staff)) {
        let creator = forum.admin || forum.staff || {};
        let creatorDetails = null;

        try {
          const creatorId = forum.admin_id || forum.staff_id;
          if (forum.admin_id) {
            creatorDetails = await getAdminById(creatorId);
            creator = creatorDetails || creator;
          } else if (forum.staff_id) {
            try {
              const staffRes = await axios.get(`${API_BASE_URL}/staff/${creatorId}`, {
                headers: getAuthHeaders(),
              });
              creatorDetails = staffRes.data?.data || staffRes.data;
              creator = creatorDetails || creator;
            } catch (staffErr) {
              try {
                creatorDetails = await getLearnersById(creatorId);
                creator = creatorDetails || creator;
              } catch (learnerErr) {
                console.error(`Failed to fetch creator ${creatorId}:`, learnerErr);
              }
            }
          }

          // Get creator details
          const creatorName = creator.full_name || 
                            creator.name || 
                            (creator.firstname && creator.lastname ? `${creator.firstname} ${creator.lastname}`.trim() : null) ||
                            (creator.first_name && creator.last_name ? `${creator.first_name} ${creator.last_name}`.trim() : null) ||
                            creator.email ||
                            "Unknown";

          const creatorPhone = creator.phone_number || creator.phone || "N/A";
          const creatorFacility = creator.facility?.name || 
                                 creator.facility_name || 
                                 (typeof creator.facility === 'string' ? creator.facility : null) ||
                                 "N/A";
          const creatorLGA = (typeof creator.lga === 'object' ? creator.lga?.name : creator.lga) || (typeof creator.local_government === 'object' ? creator.local_government?.name : creator.local_government) || "N/A";

          forum = {
            ...forum,
            creatorDetails: {
              name: creatorName,
              phone: creatorPhone,
              facility: creatorFacility,
              lga: creatorLGA,
              email: creator.email || "N/A"
            }
          };
        } catch (err) {
          console.error(`Failed to fetch creator details:`, err);
        }
      }

      // Process discussions and fetch user details
      const processedDiscussions = await Promise.all(
        (forum.discussions || []).map(async (d) => {
          // Extract user information
          let user = d.user || d.staff || d.admin || {};
          let userDetails = null;

          // If we have staff_id or admin_id but no user data or incomplete user data, fetch it
          if ((d.staff_id || d.admin_id) && (!user || !user.id || (!user.firstname && !user.first_name && !user.name && !user.full_name))) {
            try {
              const userId = d.staff_id || d.admin_id;
              if (d.staff_id) {
                // Fetch staff details
                try {
                  const staffRes = await axios.get(`${API_BASE_URL}/staff/${userId}`, {
                    headers: getAuthHeaders(),
                  });
                  userDetails = staffRes.data?.data || staffRes.data;
                  user = userDetails || user;
                } catch (staffErr) {
                  console.error(`Failed to fetch staff ${userId}:`, staffErr);
                  // Try as learner
                  try {
                    userDetails = await getLearnersById(userId);
                    user = userDetails || user;
                  } catch (learnerErr) {
                    console.error(`Failed to fetch as learner ${userId}:`, learnerErr);
                  }
                }
              } else if (d.admin_id) {
                // Fetch admin details
                userDetails = await getAdminById(userId);
                user = userDetails || user;
              }
            } catch (err) {
              console.error(`Failed to fetch user ${d.staff_id || d.admin_id}:`, err);
            }
          }

          // Get user name
          const userName = user.full_name || 
                          user.name || 
                          (user.firstname && user.lastname ? `${user.firstname} ${user.lastname}`.trim() : null) ||
                          (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}`.trim() : null) ||
                          user.email ||
                          "Admin";

          // Get facility
          const facility = user.facility?.name || 
                          user.facility_name || 
                          (typeof user.facility === 'string' ? user.facility : null) ||
                          "N/A";

          return {
            id: d.id,
            author: userName,
            authorDetails: {
              name: userName,
              facility: facility,
              phone: user.phone_number || user.phone || "N/A",
              email: user.email || "N/A"
            },
            content: d.body || "",
            created_at: d.created_at,
          };
        })
      );

      setSelectedForum(forum);
      setDiscussions(processedDiscussions);
    } catch (err) {
      console.error("Error fetching forum details:", err);
    }
  };
const handleDeleteForum = async (forumId) => {
  if (!window.confirm("Are you sure you want to delete this forum?")) return;

  try {
    await deleteForum(forumId);
    setForums(forums.filter((f) => f.id !== forumId));
    if (selectedForum?.id === forumId) {
      setSelectedForum(null);
      setDiscussions([]);
      setExpandedForumId(null);
    }
  } catch (err) {
    console.error("Error deleting forum:", err.response?.data || err);
  }
};

// Delete a discussion
const handleDeleteDiscussion = async (discussionId) => {
  if (!window.confirm("Are you sure you want to delete this discussion?")) return;

  try {
    await deleteDiscussion(discussionId);
    setDiscussions(discussions.filter((d) => d.id !== discussionId));

    // Update discussion count in the forum list
    setForums((prevForums) =>
      prevForums.map((f) =>
        f.id === selectedForum.id
          ? { ...f, discussions_count: f.discussions_count - 1 }
          : f
      )
    );
  } catch (err) {
    console.error("Error deleting discussion:", err.response?.data || err);
  }
};


 const handleAddForum = async (e) => {
  e.preventDefault();

  const authToken = localStorage.getItem("token"); // get token

  try {
    const res = await addForum({
      course_id: 1,             // or dynamically
      admin_id: currentUserId,  // set current admin id
      title: newForumTitle,
      body: newForumDescription,
      token: authToken,
    });

    const newForum = res?.data || res;
    if (newForum) {
      // Fetch creator details for the new forum
      let creatorDetails = null;
      if (newForum.admin_id || newForum.staff_id) {
        try {
          const creatorId = newForum.admin_id || newForum.staff_id;
          if (newForum.admin_id) {
            creatorDetails = await getAdminById(creatorId);
          } else if (newForum.staff_id) {
            try {
              const staffRes = await axios.get(`${API_BASE_URL}/staff/${creatorId}`, {
                headers: getAuthHeaders(),
              });
              creatorDetails = staffRes.data?.data || staffRes.data;
            } catch (staffErr) {
              try {
                creatorDetails = await getLearnersById(creatorId);
              } catch (learnerErr) {
                console.error(`Failed to fetch creator ${creatorId}:`, learnerErr);
              }
            }
          }

          if (creatorDetails) {
            const creatorName = creatorDetails.full_name || 
                              creatorDetails.name || 
                              (creatorDetails.firstname && creatorDetails.lastname ? `${creatorDetails.firstname} ${creatorDetails.lastname}`.trim() : null) ||
                              (creatorDetails.first_name && creatorDetails.last_name ? `${creatorDetails.first_name} ${creatorDetails.last_name}`.trim() : null) ||
                              creatorDetails.email ||
                              "Unknown";

            const creatorPhone = creatorDetails.phone_number || creatorDetails.phone || "N/A";
            const creatorFacility = creatorDetails.facility?.name || 
                                   creatorDetails.facility_name || 
                                   (typeof creatorDetails.facility === 'string' ? creatorDetails.facility : null) ||
                                   "N/A";
            const creatorLGA = (typeof creatorDetails.lga === 'object' ? creatorDetails.lga?.name : creatorDetails.lga) || (typeof creatorDetails.local_government === 'object' ? creatorDetails.local_government?.name : creatorDetails.local_government) || "N/A";

            newForum.creatorDetails = {
              name: creatorName,
              phone: creatorPhone,
              facility: creatorFacility,
              lga: creatorLGA,
              email: creatorDetails.email || "N/A"
            };
          }
        } catch (err) {
          console.error("Error fetching creator details for new forum:", err);
        }
      }

      setForums([...forums, newForum]);
      setNewForumTitle("");
      setNewForumDescription("");
    }
  } catch (err) {
    console.error("Error adding forum:", err);
  }
};



  const handleAddDiscussion = async (e) => {
    e.preventDefault();
    if (!selectedForum || !newDiscussion.trim()) return;
    
    try {
      const res = await addDiscussion(selectedForum.id, { body: newDiscussion });
      const newDisc = res?.data || res;
      
      if (newDisc) {
        // Fetch user details for the new discussion
        let user = newDisc.user || newDisc.staff || newDisc.admin || {};
        let userDetails = null;

        if ((newDisc.staff_id || newDisc.admin_id) && (!user || !user.id)) {
          try {
            const userId = newDisc.staff_id || newDisc.admin_id;
            if (newDisc.staff_id) {
              try {
                const staffRes = await axios.get(`${API_BASE_URL}/staff/${userId}`, {
                  headers: getAuthHeaders(),
                });
                userDetails = staffRes.data?.data || staffRes.data;
                user = userDetails || user;
              } catch (staffErr) {
                try {
                  userDetails = await getLearnersById(userId);
                  user = userDetails || user;
                } catch (learnerErr) {
                  console.error(`Failed to fetch user ${userId}:`, learnerErr);
                }
              }
            } else if (newDisc.admin_id) {
              userDetails = await getAdminById(userId);
              user = userDetails || user;
            }
          } catch (err) {
            console.error(`Failed to fetch user details:`, err);
          }
        }

        // Get user name and facility
        const userName = user.full_name || 
                        user.name || 
                        (user.firstname && user.lastname ? `${user.firstname} ${user.lastname}`.trim() : null) ||
                        (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}`.trim() : null) ||
                        user.email ||
                        "Admin";

        const facility = user.facility?.name || 
                        user.facility_name || 
                        (typeof user.facility === 'string' ? user.facility : null) ||
                        "N/A";

        // Create discussion object with user details
        const discussionWithDetails = {
          id: newDisc.id,
          author: userName,
          authorDetails: {
            name: userName,
            facility: facility,
            phone: user.phone_number || user.phone || "N/A",
            email: user.email || "N/A"
          },
          content: newDisc.body || newDiscussion,
          created_at: newDisc.created_at || new Date().toISOString(),
        };

        setDiscussions([discussionWithDetails, ...discussions]);
        setForums((prevForums) =>
          prevForums.map((f) =>
            f.id === selectedForum.id
              ? { ...f, discussions_count: (f.discussions_count || 0) + 1 }
              : f
          )
        );
        setNewDiscussion('');
      }
    } catch (err) {
      console.error("Error adding discussion:", err.response?.data || err);
    }
  };


  const filteredForums = forums.filter(
    (f) =>
      f.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleForumExpand = (forumId) => {
    if (expandedForumId === forumId) {
      setExpandedForumId(null);
      setSelectedForum(null);
      setDiscussions([]);
    } else {
      setExpandedForumId(forumId);
      const forum = forums.find(f => f.id === forumId);
      if (forum) {
        setSelectedForum(forum);
      }
      fetchForumDetails(forumId);
    }
  };

  return (
    <div className="forum-page">
      {/* LEFT PANEL */}
      <div className="forum-list">
        <div className="forum-header">
          <h2>📚 Manage Forums</h2>

          <input
            type="text"
            className="search-bar"
            placeholder="Search forums..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <form onSubmit={handleAddForum} className="forum-form">
            <input
              type="text"
              value={newForumTitle}
              onChange={(e) => setNewForumTitle(e.target.value)}
              placeholder="Forum Title"
              required
            />
            <textarea
              value={newForumDescription}
              onChange={(e) => setNewForumDescription(e.target.value)}
              placeholder="Forum Description"
              required
            />
            <button type="submit" className="btn-primary">
              + Add Forum
            </button>
          </form>
        </div>

        <ul className="forum-list-items">
          {filteredForums.length > 0 ? (
            filteredForums.map((forum) => (
              <li
                key={forum.id}
                className={`forum-card ${
                  expandedForumId === forum.id ? "active" : ""
                }`}
              >
                <div
                  className="forum-summary"
                  onClick={() => toggleForumExpand(forum.id)}
                >
                  <div className="forum-summary-header">
                    <h3>{forum.title}</h3>
                    {forum.course && (
                      <span className="forum-course-tag">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                        {forum.course?.title || forum.course?.name || "Course"}
                      </span>
                    )}
                  </div>
                  <p>{forum.description || forum.body}</p>
                  {forum.creatorDetails && (
                    <div className="forum-creator-info">
                      <div className="forum-creator-details">
                        <span className="forum-creator-name">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                          </svg>
                          {forum.creatorDetails.name}
                        </span>
                        {forum.creatorDetails.phone && forum.creatorDetails.phone !== "N/A" && (
                          <span className="forum-creator-phone">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                            </svg>
                            {forum.creatorDetails.phone}
                          </span>
                        )}
                        {forum.creatorDetails.facility && forum.creatorDetails.facility !== "N/A" && (
                          <span className="forum-creator-facility">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/>
                            </svg>
                            {forum.creatorDetails.facility}
                          </span>
                        )}
                        {forum.creatorDetails.lga && forum.creatorDetails.lga !== "N/A" && (
                          <span className="forum-creator-lga">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                            </svg>
                            {forum.creatorDetails.lga}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  <span className="forum-meta">
                    {forum.discussions_count || 0} discussions
                  </span>
                </div>
                <button
  className="btn-delete"
  onClick={(e) => {
    e.stopPropagation(); // Prevent expand toggle
    handleDeleteForum(forum.id);
  }}
>
  🗑 Delete
</button>

                {expandedForumId === forum.id && (
                  <div className="forum-preview">
                    {discussions.length > 0 ? (
                      discussions.slice(0, 3).map((d) => (
                        <p key={d.id} className="preview-discussion">
                          {d.content?.slice(0, 80)}...
                        </p>
                      ))
                    ) : (
                      <p className="empty-text">No discussions yet.</p>
                    )}
                  </div>
                )}
              </li>
            ))
          ) : (
            <p className="empty-text">No forums found.</p>
          )}
        </ul>
      </div>

      {/* RIGHT PANEL */}
      <div className="forum-details">
        {selectedForum ? (
          <>
            <div className="forum-detail-card">
              <div className="forum-detail-header">
                <div>
                  <h2>{selectedForum.title}</h2>
                  <p className="forum-description">{selectedForum.description || selectedForum.body}</p>
                  {selectedForum.creatorDetails && (
                    <div className="forum-detail-creator">
                      <div className="forum-detail-creator-header">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                        </svg>
                        <span className="forum-detail-creator-label">Created by:</span>
                        <span className="forum-detail-creator-name">{selectedForum.creatorDetails.name}</span>
                      </div>
                      <div className="forum-detail-creator-info">
                        {selectedForum.creatorDetails.phone && selectedForum.creatorDetails.phone !== "N/A" && (
                          <div className="forum-detail-creator-item">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                            </svg>
                            <span>{selectedForum.creatorDetails.phone}</span>
                          </div>
                        )}
                        {selectedForum.creatorDetails.facility && selectedForum.creatorDetails.facility !== "N/A" && (
                          <div className="forum-detail-creator-item">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/>
                            </svg>
                            <span>{selectedForum.creatorDetails.facility}</span>
                          </div>
                        )}
                        {selectedForum.creatorDetails.lga && selectedForum.creatorDetails.lga !== "N/A" && (
                          <div className="forum-detail-creator-item">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                            </svg>
                            <span>{selectedForum.creatorDetails.lga}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {selectedForum.course && (
                  <div className="forum-course-badge">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                    <span>{selectedForum.course?.title || selectedForum.course?.name || "Course"}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="discussion-section">
              <h3>💬 Discussions</h3>
              <form onSubmit={handleAddDiscussion} className="discussion-form">
                <textarea
                  value={newDiscussion}
                  onChange={(e) => setNewDiscussion(e.target.value)}
                  placeholder="Write a new discussion..."
                  required
                />
                <button type="submit" className="btn-primary">
                  Post
                </button>
              </form>

              <ul className="discussion-list">
                {discussions.length > 0 ? (
                  discussions.map((discussion) => (
                    <li key={discussion.id} className="discussion-card">
                      <div className="discussion-avatar">
                        <span>{(discussion.authorDetails?.name || discussion.author || "A")[0].toUpperCase()}</span>
                      </div>
                      <div className="discussion-content">
                        <div className="discussion-header">
                          <div className="discussion-author-info">
                            <span className="discussion-author-name">
                              {discussion.authorDetails?.name || discussion.author || "Admin"}
                            </span>
                            {discussion.authorDetails?.facility && discussion.authorDetails.facility !== "N/A" && (
                              <span className="discussion-facility">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/>
                                </svg>
                                {discussion.authorDetails.facility}
                              </span>
                            )}
                          </div>
                          <span className="discussion-time">
                            {discussion.created_at 
                              ? new Date(discussion.created_at).toLocaleString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              : "Just now"}
                          </span>
                        </div>
                        <p className="discussion-text">{discussion.content}</p>
                      </div>
                      <button
                        className="btn-delete-small"
                        onClick={() => handleDeleteDiscussion(discussion.id)}
                        title="Delete discussion"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                        </svg>
                      </button>
                    </li>
                  ))
                ) : (
                  <p className="empty-text">No discussions yet.</p>
                )}
              </ul>

            </div>
          </>
        ) : (
          <div className="forum-placeholder">
            <h3>Select a forum to view details</h3>
            <p>Click on a forum from the left panel to manage its discussions.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Forum;
